import { CombinedGraphQLErrors } from '@apollo/client/errors';
import {
  GraphQLErrorCode,
  GraphQLInternalErrorCode,
  MCP_OAUTH_CLIENT_CREDENTIALS_REQUIRED_ERROR,
} from '@this+that/constants';
import { ContinueInTerminalUnavailableReason } from '@this+that/models';
import { GraphQLFormattedError } from 'graphql';

function hasInternalErrorCode(
  error: unknown,
  code: GraphQLInternalErrorCode,
): boolean {
  if (!CombinedGraphQLErrors.is(error)) return false;
  return error.errors.some(
    (e: GraphQLFormattedError) => e.extensions?.internalErrorCode === code,
  );
}

export function getInternalErrorCode(
  error: unknown,
): GraphQLInternalErrorCode | null {
  if (!CombinedGraphQLErrors.is(error)) return null;
  for (const e of error.errors as GraphQLFormattedError[]) {
    const code = e.extensions?.internalErrorCode;
    if (code) return code as GraphQLInternalErrorCode;
  }
  return null;
}

export function getGraphQLErrorCode(error: unknown): GraphQLErrorCode | null {
  if (!CombinedGraphQLErrors.is(error)) return null;
  for (const e of error.errors as GraphQLFormattedError[]) {
    const code = e.extensions?.code;
    if (code) return code as GraphQLErrorCode;
  }
  return null;
}

export const isInvalidLabelForDoBoxGenerationError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.INVALID_LABEL_FOR_DOBOX_GENERATION,
  );

export const isEmptyContentForDoBoxGenerationError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.EMPTY_CONTENT_FOR_DOBOX_GENERATION,
  );

export const isOwnerRemovalForbiddenError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.TASK_GROUP_OWNER_REMOVAL_FORBIDDEN,
  );

export const isInsufficientTeamRoleForTriggerError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.INSUFFICIENT_TEAM_ROLE_FOR_TRIGGER,
  );

export const isUserNotMemberOfTeamError = (err: unknown) =>
  hasInternalErrorCode(err, GraphQLInternalErrorCode.USER_NOT_MEMBER_OF_TEAM);

export const isPersonCreatedTriggerNeverFiresError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.PERSON_CREATED_TRIGGER_NEVER_FIRES,
  );

export const isTeamInstagramIntegrationNotFoundError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.TEAM_INSTAGRAM_INTEGRATION_NOT_FOUND,
  );

export const isKnowledgeSpaceVersionConflictError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.KNOWLEDGE_SPACE_VERSION_CONFLICT,
  );

export const isKnowledgeSpaceForbiddenError = (err: unknown) =>
  hasInternalErrorCode(err, GraphQLInternalErrorCode.KNOWLEDGE_SPACE_FORBIDDEN);

export const isKnowledgeSpaceNotOwnerError = (err: unknown) =>
  hasInternalErrorCode(err, GraphQLInternalErrorCode.KNOWLEDGE_SPACE_NOT_OWNER);

export const isKnowledgeSpaceParticipantAlreadyExistsError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.KNOWLEDGE_SPACE_PARTICIPANT_ALREADY_EXISTS,
  );

export const isKnowledgeSpaceParticipantsNotAllowedOnUserSpaceError = (
  err: unknown,
) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.KNOWLEDGE_SPACE_PARTICIPANTS_NOT_ALLOWED_ON_USER_SPACE,
  );

export const isKnowledgeSpaceInvalidParticipantRoleError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.KNOWLEDGE_SPACE_INVALID_PARTICIPANT_ROLE,
  );

export const isKnowledgeSpaceCannotLeaveTeamSpaceError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.KNOWLEDGE_SPACE_CANNOT_LEAVE_TEAM_SPACE,
  );

export function isMcpOauthClientCredentialsRequiredError(
  error: unknown,
): boolean {
  if (!CombinedGraphQLErrors.is(error)) {
    return false;
  }
  return error.errors.some(
    (e: GraphQLFormattedError) =>
      e.extensions?.code === MCP_OAUTH_CLIENT_CREDENTIALS_REQUIRED_ERROR,
  );
}

export function getGraphQLErrorCodes(error: unknown): string[] {
  if (!CombinedGraphQLErrors.is(error)) {
    return [];
  }
  return (error.errors as GraphQLFormattedError[])
    .map((e) => e.extensions?.code)
    .filter((code): code is string => typeof code === 'string');
}

export function hasGraphQLErrorCode(
  error: unknown,
  code: GraphQLErrorCode,
): boolean {
  return getGraphQLErrorCodes(error).includes(code);
}

// The first server message carried by a user-facing code (BAD_USER_INPUT,
// CONFLICT); null for anything else so callers fall back to their own copy.
export function getUserFacingErrorMessage(error: unknown): string | null {
  if (!CombinedGraphQLErrors.is(error)) {
    return null;
  }
  const found = (error.errors as GraphQLFormattedError[]).find(
    (e) =>
      e.extensions?.code === GraphQLErrorCode.BAD_USER_INPUT ||
      e.extensions?.code === GraphQLErrorCode.CONFLICT,
  );
  return found?.message ?? null;
}

export const isTerminalFinishUnavailableError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.TERMINAL_FINISH_UNAVAILABLE,
  );

export const isTerminalMoveUndoExpiredError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.TERMINAL_MOVE_UNDO_EXPIRED,
  );

export enum TerminalFinishRefusal {
  DEVICE_OFFLINE = 'device_offline',
  NO_CONNECTION = 'no_connection',
  DAEMON_UNSUPPORTED = 'daemon_unsupported',
  UNKNOWN_SESSION = 'unknown_session',
  SESSION_BUSY = 'session_busy',
  SESSION_RUN_ACTIVE = 'session_run_active',
  TERMINAL_UNAVAILABLE = 'terminal_unavailable',
  TASK_RUN_ACTIVE = 'task_run_active',
  DISPATCH_FAILED = 'dispatch_failed',
}

const FINISH_REFUSALS: readonly string[] = Object.values(TerminalFinishRefusal);

export function getTerminalFinishRefusal(
  err: unknown,
): TerminalFinishRefusal | null {
  if (!isTerminalFinishUnavailableError(err)) {
    return null;
  }
  for (const e of (err as CombinedGraphQLErrors)
    .errors as GraphQLFormattedError[]) {
    const reason = e.extensions?.reason;
    if (typeof reason === 'string' && FINISH_REFUSALS.includes(reason)) {
      return reason as TerminalFinishRefusal;
    }
  }
  return null;
}

export const isContinueInTerminalUnavailableError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.CONTINUE_IN_TERMINAL_UNAVAILABLE,
  );

export const isTaskTreeMoveBlockedError = (err: unknown) =>
  hasInternalErrorCode(err, GraphQLInternalErrorCode.TASK_TREE_MOVE_BLOCKED);

export const isTaskTreeCycleError = (err: unknown) =>
  hasInternalErrorCode(err, GraphQLInternalErrorCode.TASK_TREE_CYCLE);

export const isTaskTreeDepthExceededError = (err: unknown) =>
  hasInternalErrorCode(err, GraphQLInternalErrorCode.TASK_TREE_DEPTH_EXCEEDED);

export const isTaskTreeSizeExceededError = (err: unknown) =>
  hasInternalErrorCode(err, GraphQLInternalErrorCode.TASK_TREE_SIZE_EXCEEDED);

export const isDelegationHeldByTerminalError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.DELEGATION_HELD_BY_TERMINAL,
  );

const CONTINUE_REFUSAL_REASONS: readonly string[] = Object.values(
  ContinueInTerminalUnavailableReason,
);

export function getContinueInTerminalRefusal(
  err: unknown,
): ContinueInTerminalUnavailableReason | null {
  if (!isContinueInTerminalUnavailableError(err)) {
    return null;
  }
  for (const e of (err as CombinedGraphQLErrors)
    .errors as GraphQLFormattedError[]) {
    const reason = e.extensions?.reason;
    if (
      typeof reason === 'string' &&
      CONTINUE_REFUSAL_REASONS.includes(reason)
    ) {
      return reason as ContinueInTerminalUnavailableReason;
    }
  }
  return null;
}
